import { useState, useEffect, useMemo } from 'react'
import { Table, Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { apiService } from '@/service/api'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { markdownComponents } from '@/config/markdownConfig'
import { useTranslation } from '@/i18n'
import type { DocumentVisualElements, VisualElementSummary } from '@/types/visual-elements'
import type { ExtractedTablesResponse, ExtractedTable } from '@/types/extracted-tables'

interface ExtractionsTabProps {
  currentPage?: number
  searchQuery?: string
  pageText: string | null
  documentId: string
  onElementClick?: (pageNumber: number) => void
  documentStatus?: string
}


// Parse markdown tables from text
function parseMarkdownTables(markdown: string): Array<{ content: string; index: number }> {
  if (!markdown) return []

  const tables: Array<{ content: string; index: number }> = []
  const lines = markdown.split('\n')
  let currentTable: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]
    // Check if line is part of a table (contains |)
    if (line.trim().includes('|')) {
      currentTable.push(line)
    } else if (currentTable.length > 0) {
      // End of table found
      const tableContent = currentTable.join('\n')
      // Verify it's a valid markdown table (has separator row with ---)
      if (tableContent.includes('---')) {
        tables.push({ content: tableContent, index: tables.length })
      }
      currentTable = []
    }
  }

  // Handle table at end of document
  if (currentTable.length > 0) {
    const tableContent = currentTable.join('\n')
    if (tableContent.includes('---')) {
      tables.push({ content: tableContent, index: tables.length })
    }
  }

  return tables
}

// Visual element card component for tables
interface TableElementCardProps {
  element: VisualElementSummary
  onClick?: (pageNumber: number) => void
}

const VisualTableCard: React.FC<TableElementCardProps> = ({ element, onClick }) => {
  const { t } = useTranslation()
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [imageError, setImageError] = useState(false)

  // Load image through authenticated API
  useEffect(() => {
    let blobUrl: string | null = null
    let isCancelled = false

    const loadImage = async () => {
      if (!element.has_image) return

      try {
        const blob = await apiService.getVisualElementImage(element.id, false)

        // Only create blob URL if this effect hasn't been cancelled
        if (!isCancelled) {
          blobUrl = URL.createObjectURL(blob)
          setImageUrl(blobUrl)
          setImageError(false)
        } else if (blob) {
          // If cancelled, revoke the blob URL immediately
          const tempUrl = URL.createObjectURL(blob)
          URL.revokeObjectURL(tempUrl)
        }
      } catch (error) {
        if (!isCancelled) {
          console.error(`Failed to load image for element ${element.id}:`, error)
          setImageError(true)
        }
      }
    }

    loadImage()

    return () => {
      isCancelled = true
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl)
      }
    }
  }, [element.id, element.has_image])

  const handleClick = () => {
    onClick?.(element.page_number)
  }

  return (
    <div
      className="p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border"
      onClick={handleClick}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Table className="w-4 h-4 text-gray-700 dark:text-gray-300" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('document_viewer:extractions.visualTable', { defaultValue: 'Visual Table' })}</span>
          <Badge variant="secondary" className="text-xs">
            {t('document_viewer:extractions.page', { number: element.page_number, defaultValue: 'Page {{number}}' })}
          </Badge>
        </div>
      </div>

      {/* Image */}
      {element.has_image && (
        <div className="mb-3 bg-white dark:bg-dark-card rounded border dark:border-dark-border overflow-hidden">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={element.caption || t('document_viewer:extractions.tableOnPage', { number: element.page_number, defaultValue: 'Table on page {{number}}' })}
              className="w-full h-auto object-contain p-2"
            />
          ) : imageError ? (
            <div className="w-full h-48 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              {t('document_viewer:extractions.tableImageLoadFailed', { defaultValue: 'Failed to load table image' })}
            </div>
          ) : (
            <div className="w-full h-48 flex items-center justify-center text-gray-400 dark:text-gray-500 text-sm">
              {t('document_viewer:extractions.loadingTableImage', { defaultValue: 'Loading table image...' })}
            </div>
          )}
        </div>
      )}

      {/* Caption */}
      {element.caption && (
        <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">{element.caption}</p>
      )}
    </div>
  )
}

interface StructuredTableCardProps {
  table: ExtractedTable
  index: number
}

const StructuredTableCard: React.FC<StructuredTableCardProps> = ({ table, index }) => {
  const { t } = useTranslation()
  const markdownContent = useMemo(() => {
    if (!table.rows || table.rows.length === 0) return ''

    const escapeCell = (text: string): string => {
      if (!text) return ''
      return text.replace(/\|/g, '\\|').replace(/\r\n|\r|\n/g, ' ').trim()
    }

    const lines: string[] = []

    if (table.headers && table.headers.length > 0) {
      lines.push('| ' + table.headers.map(h => escapeCell(h || '')).join(' | ') + ' |')
      lines.push('|' + table.headers.map(() => '---').join('|') + '|')
    } else if (table.rows.length > 0) {
      const firstRow = table.rows[0]
      lines.push('| ' + firstRow.map(c => escapeCell(c || '')).join(' | ') + ' |')
      lines.push('|' + firstRow.map(() => '---').join('|') + '|')
    }

    const startIdx = (table.headers && table.headers.length > 0) ? 0 : 1
    for (let i = startIdx; i < table.rows.length; i++) {
      const row = table.rows[i]
      lines.push('| ' + row.map(c => escapeCell(c || '')).join(' | ') + ' |')
    }

    return lines.join('\n')
  }, [table])
  
  const confidenceColor = table.confidence >= 0.8 ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400' :
                          table.confidence >= 0.5 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400' :
                          'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
  
  return (
    <div className="p-4 border rounded-lg bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Table className="w-4 h-4 text-blue-600 dark:text-blue-400" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {table.title || t('document_viewer:extractions.tableN', { number: index + 1, defaultValue: 'Table {{number}}' })}
          </span>
          <Badge variant="secondary" className="text-xs">
            {t('document_viewer:extractions.page', { number: table.page_number, defaultValue: 'Page {{number}}' })}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {table.num_rows}×{table.num_columns}
          </Badge>
        </div>
        <Badge className={`text-xs ${confidenceColor}`}>
          {t('document_viewer:extractions.confidenceLower', { percent: Math.round(table.confidence * 100), defaultValue: '{{percent}}% confidence' })}
        </Badge>
      </div>

      {/* Rendered table */}
      {markdownContent ? (
        <div className="prose max-w-none overflow-x-auto">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={markdownComponents}
          >
            {markdownContent}
          </ReactMarkdown>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-dark-text-muted italic">{t('document_viewer:extractions.noTableContent', { defaultValue: 'No table content available' })}</p>
      )}
      
      {/* Caption */}
      {table.caption && (
        <p className="text-xs text-gray-500 dark:text-dark-text-muted mt-2 italic">{table.caption}</p>
      )}
    </div>
  )
}

interface MarkdownTableCardProps {
  table: { content: string; index: number }
}

const MarkdownTableCard: React.FC<MarkdownTableCardProps> = ({ table }) => {
  const { t } = useTranslation()
  return (
    <div className="p-4 border rounded-lg bg-white dark:bg-dark-card border-gray-200 dark:border-dark-border">
      {/* Header */}
      <div className="flex items-center gap-2 mb-3">
        <Table className="w-4 h-4 text-gray-700 dark:text-gray-300" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{t('document_viewer:extractions.tableN', { number: table.index + 1, defaultValue: 'Table {{number}}' })}</span>
      </div>

      {/* Rendered table */}
      <div className="prose max-w-none">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={markdownComponents}
        >
          {table.content}
        </ReactMarkdown>
      </div>
    </div>
  )
}

export function ExtractionsTabLegacy({ pageText, documentId, onElementClick }: ExtractionsTabProps) {
  const { t } = useTranslation()
  const [visualElements, setVisualElements] = useState<DocumentVisualElements | null>(null)
  const [structuredTables, setStructuredTables] = useState<ExtractedTablesResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')

  const markdownTables = useMemo(() => {
    return parseMarkdownTables(pageText || '')
  }, [pageText])

  useEffect(() => {
    if (!documentId) {
      return
    }

    const loadData = async () => {
      setLoading(true)
      try {
        const [visualData, tablesData] = await Promise.all([
          apiService.getDocumentVisualElements(documentId).catch(err => {
            console.error('Failed to load visual elements:', err)
            return null
          }),
          apiService.getDocumentExtractedTables(documentId).catch(err => {
            console.error('Failed to load structured tables:', err)
            return null
          })
        ])
        
        setVisualElements(visualData)
        setStructuredTables(tablesData)
      } finally {
        setLoading(false)
      }
    }

    loadData()
  }, [documentId])

  const filteredVisualTables = useMemo(() => {
    if (!visualElements?.tables) return []

    if (!searchTerm) return visualElements.tables

    const search = searchTerm.toLowerCase()
    return visualElements.tables.filter(table =>
      table.caption?.toLowerCase().includes(search)
    )
  }, [visualElements, searchTerm])
  
  const filteredStructuredTables = useMemo(() => {
    if (!structuredTables?.tables) return []
    
    if (!searchTerm) return structuredTables.tables
    
    const search = searchTerm.toLowerCase()
    return structuredTables.tables.filter(table =>
      table.title?.toLowerCase().includes(search) ||
      table.caption?.toLowerCase().includes(search) ||
      table.rows.some(row => row.some(cell => cell?.toLowerCase()?.includes(search)))
    )
  }, [structuredTables, searchTerm])

  const filteredMarkdownTables = useMemo(() => {
    if (!markdownTables.length) return []
    
    if (!searchTerm) return markdownTables
    
    const search = searchTerm.toLowerCase()
    return markdownTables.filter(table =>
      table.content.toLowerCase().includes(search)
    )
  }, [markdownTables, searchTerm])

  const hasStructuredTables = filteredStructuredTables.length > 0
  const totalTableCount = hasStructuredTables 
    ? filteredStructuredTables.length 
    : (filteredVisualTables?.length || 0) + filteredMarkdownTables.length

  const renderTablesContent = () => {
    if (loading && markdownTables.length === 0 && !hasStructuredTables) {
      return (
        <div className="text-center py-12 text-gray-500 dark:text-dark-text-muted">
          <div className="flex flex-col items-center gap-2">
            <div className="animate-pulse">
              <Table className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p>{t('document_viewer:extractions.loadingTables', { defaultValue: 'Loading tables...' })}</p>
          </div>
        </div>
      )
    }

    const hasVisualTables = filteredVisualTables && filteredVisualTables.length > 0
    const hasMarkdownTables = filteredMarkdownTables.length > 0

    if (!hasVisualTables && !hasMarkdownTables && !hasStructuredTables) {
      return (
        <div className="text-center py-12 text-gray-500 dark:text-dark-text-muted">
          <div className="flex flex-col items-center gap-2">
            <Table className="w-8 h-8 text-gray-300 dark:text-gray-600" />
            <p className="text-sm">{t('document_viewer:extractions.noTables', { defaultValue: 'No tables found in this document.' })}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {t('document_viewer:extractions.noTablesHintLegacy', { defaultValue: 'Tables will appear here once extracted from the document.' })}
            </p>
          </div>
        </div>
      )
    }

    return (
      <div className="space-y-4">
        {/* Search bar */}
        {(hasStructuredTables || hasVisualTables) && (
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
            <Input
              placeholder={t('document_viewer:extractions.searchPlaceholder', { defaultValue: 'Search tables...' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        )}

        {/* Count */}
        <div className="text-sm text-gray-600 dark:text-gray-400">
          {t('document_viewer:extractions.tablesFound', { count: totalTableCount, defaultValue: '{{count}} tables found' })}
          {structuredTables && structuredTables.overall_confidence > 0 && (
            <span className="text-gray-400 dark:text-gray-500 ml-2">
              {t('document_viewer:extractions.avgConfidence', { percent: Math.round(structuredTables.overall_confidence * 100), defaultValue: '(avg. confidence: {{percent}}%)' })}
            </span>
          )}
        </div>

        {/* Structured tables section (from extraction API) - prioritized */}
        {hasStructuredTables && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-blue-700 dark:text-blue-400 flex items-center gap-2">
              <Table className="w-4 h-4" />
              {t('document_viewer:extractions.sectionExtracted', { defaultValue: 'Extracted Tables' })}
              <Badge variant="outline" className="text-xs">
                {t('document_viewer:extractions.structuredCount', { count: filteredStructuredTables.length, defaultValue: '{{count}} structured' })}
              </Badge>
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {filteredStructuredTables.map((table, idx) => (
                <StructuredTableCard key={table.table_id} table={table} index={idx} />
              ))}
            </div>
          </div>
        )}

        {/* Markdown tables section (fallback) */}
        {hasMarkdownTables && !hasStructuredTables && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Table className="w-4 h-4" />
              {t('document_viewer:extractions.sectionTextBased', { defaultValue: 'Text-Based Tables' })}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {filteredMarkdownTables.map((table) => (
                <MarkdownTableCard key={table.index} table={table} />
              ))}
            </div>
          </div>
        )}

        {/* Visual tables section (AI-detected from images) */}
        {hasVisualTables && !hasStructuredTables && (
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
              <Table className="w-4 h-4" />
              {t('document_viewer:extractions.sectionVisual', { defaultValue: 'Visual Tables (AI-Detected)' })}
            </h3>
            <div className="grid grid-cols-1 gap-4">
              {filteredVisualTables.map((table) => (
                <VisualTableCard
                  key={table.id}
                  element={table}
                  onClick={onElementClick}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="h-full overflow-auto bg-white dark:bg-dark-card p-6">
      {renderTablesContent()}
    </div>
  )
}
